import * as Result from './Result';

// Duplicamos el tipo para no tener dependencia circular sobre core/api
// Dependencia:  core/lib/api/index.ts > core/lib/api/Res.ts > core/lib/infrastructure/Localize.ts > core/lib/infrastructure/Events.ts > core/lib/infrastructure/Errors.ts
enum TicketLineStatus {
	New = 0,
	PendingPreparation = 1,
	Cooking = 2,
	ReadyToServe = 3,
	Served = 4
}
export const TypeSymbol = Symbol('Error::TypeSymbol');

export function isCustomError(error: any): error is CustomError {
	return error[TypeSymbol] !== undefined;
}

export abstract class CustomError extends Error {
	abstract [TypeSymbol]: string;

	isDomainError = (): this is DomainError => this[TypeSymbol] === 'DomainError';
	isInvalidCustomerCifError = (): this is InvalidCustomerCifError => this[TypeSymbol] === 'InvalidCustomerCifError';
	isInvalidPaymentError = (): this is InvalidPaymentError => this[TypeSymbol] === 'InvalidPaymentError';
	isInvalidPriceListError = (): this is InvalidPriceListError => this[TypeSymbol] === 'InvalidPriceListError';
	isPrintError = (): this is PrintError => this[TypeSymbol] === 'PrintError';
	isProductNotAvailableError = (): this is ProductNotAvailableError => this[TypeSymbol] === 'ProductNotAvailableError';
	isServerError = (): this is ServerError => this[TypeSymbol] === 'ServerError';
	isVersionCompatibilityError = (): this is VersionCompatibilityError => this[TypeSymbol] === 'VersionCompatibilityError';
	isExcelIntegrationError = (): this is ExcelIntegrationError => this[TypeSymbol] === 'ExcelIntegrationError';
	isFrozenPaymentsError = (): this is FrozenPaymentsError => this[TypeSymbol] === 'FrozenPaymentsError';
	isSyncError = (): this is SyncError => this[TypeSymbol] === 'SyncError';
	isTicketLineNotFoundError = (): this is TicketLineNotFoundError => this[TypeSymbol] === 'TicketLineNotFoundError';
	isTicketSynchronizationError = (): this is TicketSynchronizationError => this[TypeSymbol] === 'TicketSynchronizationError';
	isTicketNotFoundError = (): this is TicketNotFoundError => this[TypeSymbol] === 'TicketNotFoundError';
	isLicenseError = (): this is LicenseError => this[TypeSymbol] === 'LicenseError';
	isInvalidTicketLineStateError = (): this is InvalidTicketLineStateError => this[TypeSymbol] === 'InvalidTicketLineStateError';
	isEmptyTicketError = (): this is EmptyTicketError => this[TypeSymbol] === 'EmptyTicketError';
	isFrozenTicketError = (): this is FrozenTicketError => this[TypeSymbol] === 'FrozenTicketError';
	isFrozenDeliveryNoteError = (): this is FrozenDeliveryNoteError => this[TypeSymbol] === 'FrozenDeliveryNoteError';
	isFrozenSalesOrderError = (): this is FrozenSalesOrderError => this[TypeSymbol] === 'FrozenSalesOrderError';
	isDeliveryNoteNotFoundError = (): this is DeliveryNoteNotFoundError => this[TypeSymbol] === 'DeliveryNoteNotFoundError';
	isIntegrationAuthorizationError = (): this is IntegrationAuthorizationError => this[TypeSymbol] === 'IntegrationAuthorizationError';
}

export class DomainError extends CustomError {
	[TypeSymbol] = 'DomainError';
	'disable_structural_typing': 'DomainError';
}

export class InvalidCustomerCifError extends CustomError {
	[TypeSymbol] = 'InvalidCustomerCifError';
	'disable_structural_typing': 'InvalidCustomerCifError';
}

export class InvalidPaymentError extends CustomError {
	[TypeSymbol] = 'InvalidPaymentError';
	'disable_structural_typing': 'InvalidPaymentError';
}

export class InvalidPriceListError extends CustomError {
	[TypeSymbol] = 'InvalidPriceListError';
	'disable_structural_typing': 'InvalidPriceListError';
}

export class PrintError extends CustomError {
	[TypeSymbol] = 'PrintError';
	'disable_structural_typing': 'PrintError';
}

export class ProductNotAvailableError extends CustomError {
	[TypeSymbol] = 'ProductNotAvailableError';
	'disable_structural_typing': 'ProductNotAvailableError';
}

export class ServerError extends CustomError {
	[TypeSymbol] = 'ServerError';
	'disable_structural_typing': 'ServerError';
}

export class VersionCompatibilityError extends CustomError {
	[TypeSymbol] = 'VersionCompatibilityError';
	'disable_structural_typing': 'VersionCompatibilityError';
}

export class ExcelIntegrationError extends CustomError {
	[TypeSymbol] = 'ExcelIntegrationError';
	'disable_structural_typing': 'ExcelIntegrationError';
}

export class FrozenPaymentsError extends CustomError {
	[TypeSymbol] = 'FrozenPaymentsError';
	'disable_structural_typing': 'FrozenPaymentsError';
}

export enum SyncErrorCode {
	InvalidLicense = 1,
	InvalidMachineId = 2,
	ClientImportError = 3,
	HubExportError = 4,
	DeserializationError = 5,
	InvalidConnectionToken = 6,
	ProcessUploadedDataError = 7,
	CommunicationsDisabled = 8,
	IncompatibleVersion = 9,
	ScaleUpdateError = 10
}

export class SyncError extends CustomError {
	[TypeSymbol] = 'SyncError';
	'disable_structural_typing': 'SyncError';

	constructor(readonly code: SyncErrorCode, message: string) {
		super(message);
	}
}

export class TicketLineNotFoundError extends CustomError {
	[TypeSymbol] = 'TicketLineNotFoundError';
	'disable_structural_typing': 'TicketLineNotFoundError';

	constructor(readonly ticketId: number, readonly ticketLineId: number, readonly ticketLineGlobalId: string, message: string) {
		super(message);
	}
}

export class TicketSynchronizationError extends CustomError {
	[TypeSymbol] = 'TicketSynchronizationError';
	'disable_structural_typing': 'TicketSynchronizationError';

	constructor(readonly ticketId: number, message: string) {
		super(message);
	}
}

export class TicketNotFoundError extends CustomError {
	[TypeSymbol] = 'TicketNotFoundError';
	'disable_structural_typing': 'TicketNotFoundError';

	constructor(readonly ticketId: number, readonly ticketGlobalId: string, message: string) {
		super(message);
	}
}

export class DeliveryNoteNotFoundError extends CustomError {
	[TypeSymbol] = 'DeliveryNoteNotFoundError';
	'disable_structural_typing': 'DeliveryNoteNotFoundError';

	constructor(readonly noteId: number, message: string) {
		super(message);
	}
}

export enum IntegrationAuthorizationErrorType {
	Unauthorized,
	Forbidden
}

export class IntegrationAuthorizationError extends CustomError {
	[TypeSymbol] = 'IntegrationAuthorizationError';
	'disable_structural_typing': 'IntegrationAuthorizationError';

	constructor(readonly errorType: IntegrationAuthorizationErrorType, message: string) {
		super(message);
	}
}

export enum LicenseErrorType {
	KillApplication,
	AbortOperation
}

export class LicenseError extends CustomError {
	[TypeSymbol] = 'LicenseError';
	'disable_structural_typing': 'LicenseError';

	constructor(readonly errorType: LicenseErrorType, message: string) {
		super(message);
	}
}

export class InvalidTicketLineStateError extends CustomError {
	[TypeSymbol] = 'InvalidTicketLineStateError';
	'disable_structural_typing': 'InvalidTicketLineStateError';

	constructor(readonly ticketId: number, readonly lineStatus: TicketLineStatus, message: string) {
		super(message);
	}
}

export class EmptyTicketError extends CustomError {
	[TypeSymbol] = 'EmptyTicketError';
	'disable_structural_typing': 'EmptyTicketError';

	constructor(readonly ticketId: number, message: string) {
		super(message);
	}
}

export class FrozenTicketError extends CustomError {
	[TypeSymbol] = 'FrozenTicketError';
	'disable_structural_typing': 'FrozenTicketError';

	constructor(readonly ticketId: number, message: string) {
		super(message);
	}
}

export class FrozenDeliveryNoteError extends CustomError {
	[TypeSymbol] = 'FrozenDeliveryNoteError';
	'disable_structural_typing': 'FrozenDeliveryNoteError';

	constructor(readonly noteId: number, message: string) {
		super(message);
	}
}

export class FrozenSalesOrderError extends CustomError {
	[TypeSymbol] = 'FrozenSalesOrderError';
	'disable_structural_typing': 'FrozenSalesOrderError';

	constructor(readonly orderId: number, message: string) {
		super(message);
	}
}

export type ErrorHandlers = {
	onDomainError?: (error: DomainError) => Promise<void>,
	onEmptyTicketError?: (error: EmptyTicketError) => Promise<void>,
	onExcelIntegrationError?: (error: ExcelIntegrationError) => Promise<void>,
	onFrozenDeliveryNoteError?: (error: FrozenDeliveryNoteError) => Promise<void>,
	onFrozenPaymentsError?: (error: FrozenPaymentsError) => Promise<void>,
	onFrozenSalesOrderError?: (error: FrozenSalesOrderError) => Promise<void>,
	onFrozenTicketError?: (error: FrozenTicketError) => Promise<void>,
	onInvalidCustomerCifError?: (error: InvalidCustomerCifError) => Promise<void>,
	onInvalidPaymentError?: (error: InvalidPaymentError) => Promise<void>,
	onInvalidPriceListError?: (error: InvalidPriceListError) => Promise<void>,
	onInvalidTicketLineStateError?: (error: InvalidTicketLineStateError) => Promise<void>,
	onLicenseError?: (error: LicenseError) => Promise<void>,
	onPrintError?: (error: PrintError) => Promise<void>,
	onProductNotAvailableError?: (error: ProductNotAvailableError) => Promise<void>,
	onServerError?: (error: ServerError) => Promise<void>,
	onSyncError?: (error: SyncError) => Promise<void>,
	onTicketLineNotFoundError?: (error: TicketLineNotFoundError) => Promise<void>,
	onTicketNotFoundError?: (error: TicketNotFoundError) => Promise<void>,
	onTicketSynchronizationError?: (error: TicketSynchronizationError) => Promise<void>,
	onVersionCompatibilityError?: (error: VersionCompatibilityError) => Promise<void>,
	onDeliveryNoteNotFoundError?: (error: DeliveryNoteNotFoundError) => Promise<void>,
	onIntegrationAuthorizationError?: (error: IntegrationAuthorizationError) => Promise<void>;
};

export async function handleServerErrors<T>(fn: (...args: any) => Promise<T>, handlers: ErrorHandlers): Promise<Result<T, CustomError>> {
	try {
		const result = await fn();
		return Result.ok(result);
	}
	catch (error) {
		// Si no es un error enviado el servidor de Ágora, lo relanzo
		if (!isCustomError(error))
			throw error;

		if (error.isDomainError() && handlers.onDomainError) {
			await handlers.onDomainError(error);
			return Result.error(error);
		}

		if (error.isEmptyTicketError() && handlers.onEmptyTicketError) {
			await handlers.onEmptyTicketError(error);
			return Result.error(error);
		}

		if (error.isExcelIntegrationError() && handlers.onExcelIntegrationError) {
			await handlers.onExcelIntegrationError(error);
			return Result.error(error);
		}

		if (error.isFrozenDeliveryNoteError() && handlers.onFrozenDeliveryNoteError) {
			await handlers.onFrozenDeliveryNoteError(error);
			return Result.error(error);
		}

		if (error.isFrozenPaymentsError() && handlers.onFrozenPaymentsError) {
			await handlers.onFrozenPaymentsError(error);
			return Result.error(error);
		}

		if (error.isFrozenSalesOrderError() && handlers.onFrozenSalesOrderError) {
			await handlers.onFrozenSalesOrderError(error);
			return Result.error(error);
		}

		if (error.isFrozenTicketError() && handlers.onFrozenTicketError) {
			await handlers.onFrozenTicketError(error);
			return Result.error(error);
		}

		if (error.isInvalidCustomerCifError() && handlers.onInvalidCustomerCifError) {
			await handlers.onInvalidCustomerCifError(error);
			return Result.error(error);
		}

		if (error.isInvalidPaymentError() && handlers.onInvalidPaymentError) {
			await handlers.onInvalidPaymentError(error);
			return Result.error(error);
		}

		if (error.isInvalidPriceListError() && handlers.onInvalidPriceListError) {
			await handlers.onInvalidPriceListError(error);
			return Result.error(error);
		}

		if (error.isInvalidTicketLineStateError() && handlers.onInvalidTicketLineStateError) {
			await handlers.onInvalidTicketLineStateError(error);
			return Result.error(error);
		}

		if (error.isLicenseError() && handlers.onLicenseError) {
			await handlers.onLicenseError(error);
			return Result.error(error);
		}

		if (error.isPrintError() && handlers.onPrintError) {
			await handlers.onPrintError(error);
			return Result.error(error);
		}

		if (error.isProductNotAvailableError() && handlers.onProductNotAvailableError) {
			await handlers.onProductNotAvailableError(error);
			return Result.error(error);
		}

		if (error.isServerError() && handlers.onServerError) {
			await handlers.onServerError(error);
			return Result.error(error);
		}

		if (error.isTicketSynchronizationError() && handlers.onTicketSynchronizationError) {
			await handlers.onTicketSynchronizationError(error);
			return Result.error(error);
		}

		if (error.isSyncError() && handlers.onSyncError) {
			await handlers.onSyncError(error);
			return Result.error(error);
		}

		if (error.isTicketLineNotFoundError() && handlers.onTicketLineNotFoundError) {
			await handlers.onTicketLineNotFoundError(error);
			return Result.error(error);
		}

		if (error.isTicketNotFoundError() && handlers.onTicketNotFoundError) {
			await handlers.onTicketNotFoundError(error);
			return Result.error(error);
		}

		if (error.isVersionCompatibilityError() && handlers.onVersionCompatibilityError) {
			await handlers.onVersionCompatibilityError(error);
			return Result.error(error);
		}

		if (error.isDeliveryNoteNotFoundError() && handlers.onDeliveryNoteNotFoundError) {
			await handlers.onDeliveryNoteNotFoundError(error);
			return Result.error(error);
		}

		if (error.isIntegrationAuthorizationError() && handlers.onIntegrationAuthorizationError) {
			await handlers.onIntegrationAuthorizationError(error);
			return Result.error(error);
		}

		throw error;
	}
}
